// Funciones de empresas: vinculaciones, trabajadores, regionales, horarios y profesiogramas.
// index.js las pone en el state: se usan como this.$store.state.<función>(...). Son métodos, así que `this` es el state.
import { db } from "@/firebase";
import {
  doc,
  query,
  collection,
  where,
  getDocs,
  serverTimestamp,
  setDoc,
  limit,
} from "firebase/firestore";
import localforage from "localforage";
import { colecciones } from "../constantes";

export const funcionesEmpresas = {
  // OBTENER TRABAJADORES
  async obtenerTrabajadores(idEmpresaSeleccionada, mostrarCaracteres) {
    const q = query(
      collection(db, colecciones.firebase.empresa.trabajadores.registros),
      where("idEmpresa", "==", idEmpresaSeleccionada),
      where("estado", "==", "Vinculado"),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    const registros = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const obtenerUsuario = await this.obtenerUsuario(
          doc.data().idUsuario,
        );
        if (obtenerUsuario) {
          let cargo = "SIN CARGO ASIGNADO";
          const obtenerCargo = await this.obtenerCargo(doc.data().idCargo);
          if (obtenerCargo) {
            cargo = obtenerCargo.nombre.toUpperCase();
          }
          return {
            id: doc.id,
            idUsuario: doc.data().idUsuario,
            nombre: `${obtenerUsuario.nombre.toUpperCase()} ${obtenerUsuario.apellido.toUpperCase()}`,
            numeroIdentificacion: mostrarCaracteres
              ? obtenerUsuario.documento.numero.toString()
              : this.ocultarCaracteres(
                obtenerUsuario.documento.numero.toString(),
                3,
              ),
            correo: mostrarCaracteres
              ? obtenerUsuario.correo.toUpperCase()
              : this.ocultarCorreo(obtenerUsuario.correo.toUpperCase()),
            foto: obtenerUsuario.foto.url,
            cargo: cargo,
            privilegios: doc.data().privilegios,
            estado: doc.data().estado,
            telefono: obtenerUsuario.telefono,
          };
        } else {
          return null;
        }
      }),
    );
    const trabajadores = registros.filter((item) => item !== null);
    return trabajadores ? trabajadores : [];
  },
  async obtenerEmpresa(id) {
    const q = query(
      collection(db, "riskmann_empresa"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  // OBTENER VINCULACIONES DONDE EL USUARIO POSEA EL PRIVILEGIO PANEL ADMINISTRATIVO.VER EN TRUE
  async obtenerVinculacionesPlanesAccion(idUsuario) {
    const q = query(
      collection(db, "riskmann_trabajadores"),
      where("idUsuario", "==", idUsuario),
      where("estado", "==", "Vinculado"),
      where("privilegios.panelAdministrativo.ver", "==", true),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs.map((doc) => doc.data())
      : null;
  },
  async obtenerEmpresaPrincipal(nit) {
    const q = query(
      collection(db, "riskmann_empresa"),
      where("nit.numero", "==", nit),
      where("nit.consecutivo", "==", 0),
      limit(1),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerCargo(id) {
    const q = query(
      collection(db, "riskmann_empresas_profesiogramas_cargos"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerExamenProfesiograma(id) {
    const q = query(
      collection(db, "riskmann_empresas_profesiogramas_examenes"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerProfesiograma(id) {
    const q = query(
      collection(db, "riskmann_empresas_profesiogramas"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerHorario(id) {
    const q = query(
      collection(db, "riskmann_empresas_horarios"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerCategoriaCargo(id) {
    const q = query(
      collection(db, "riskmann_empresas_profesiogramas_categorias_cargos"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerRegional(id) {
    const q = query(
      collection(db, "riskmann_regionales"),
      where("id", "==", id),
      where("registro.estado", "in", ["Creado", "Editado"]),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length > 0
      ? querySnapshot.docs[0].data()
      : null;
  },
  async obtenerVinculacion(idEmpresa, idUsuario, modoSinConexion) {
    if (modoSinConexion || !navigator.onLine) {
      const registros = await localforage.getItem(
        colecciones.localforage.empresa.trabajadores.registros,
      );
      const registro = Array.isArray(registros)
        ? registros.find((item) => item && (item.idUsuario === idUsuario || item.id === idUsuario))
        : null;
      return registro ? registro : null;
    } else {
      try {
        const q = query(
          collection(db, colecciones.firebase.empresa.trabajadores.registros),
          where("idEmpresa", "==", idEmpresa),
          where("idUsuario", "==", idUsuario),
          where("registro.estado", "in", ["Creado", "Editado"]),
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.length > 0
          ? querySnapshot.docs[0].data()
          : null;
      } catch (e) {
        console.warn("Falla de red en obtenerVinculacion, usando local:", e);
        const registros = await localforage.getItem(
          colecciones.localforage.empresa.trabajadores.registros,
        );
        return Array.isArray(registros)
          ? registros.find((item) => item && (item.idUsuario === idUsuario || item.id === idUsuario)) || null
          : null;
      }
    }
  },
  // OBTENER TRABAJADOR
  async obtenerTrabajador(idUsuario, idEmpresa, modoSinConexion) {
    if (modoSinConexion || !navigator.onLine) {
      const registros = await localforage.getItem(
        colecciones.localforage.empresa.trabajadores.registros,
      );
      const registro = Array.isArray(registros)
        ? registros.find((item) => item && (item.idUsuario === idUsuario || item.id === idUsuario))
        : null;
      return registro ? registro : null;
    } else {
      try {
        const obtenerVinculacion = await this.obtenerVinculacion(
          idEmpresa,
          idUsuario,
        );
        const obtenerUsuario = await this.obtenerUsuario(idUsuario);
        if (obtenerUsuario && obtenerVinculacion) {
          let cargo = "SIN CARGO ASIGNADO";
          try {
            if (obtenerVinculacion.idCargo) {
              const obtenerCargo = await this.obtenerCargo(
                obtenerVinculacion.idCargo,
              );
              if (obtenerCargo) {
                cargo = obtenerCargo.nombre.toUpperCase();
              }
            }
          } catch (e) {
            console.warn("No se pudo obtener cargo del trabajador:", e);
          }
          const data = {
            id: obtenerVinculacion.id,
            idUsuario: idUsuario,
            // Para que la copia del equipo distinga el espacio (los seleccionables solo precargan los de su espacio)
            idEmpresa: obtenerVinculacion.idEmpresa || idEmpresa,
            nombre: `${obtenerUsuario.nombre ? obtenerUsuario.nombre.toUpperCase() : ''} ${obtenerUsuario.apellido ? obtenerUsuario.apellido.toUpperCase() : ''}`.trim(),
            numeroIdentificacion: this.ocultarCaracteres(
              obtenerUsuario.documento && obtenerUsuario.documento.numero ? obtenerUsuario.documento.numero.toString() : '',
              3,
            ),
            correo: this.ocultarCorreo(obtenerUsuario.correo ? obtenerUsuario.correo.toUpperCase() : ''),
            foto: obtenerUsuario.foto && obtenerUsuario.foto.url ? obtenerUsuario.foto.url : '',
            cargo: cargo,
            telefono: obtenerUsuario.telefono || '',
          };
          if (data) {
            localforage.getItem(colecciones.localforage.empresa.trabajadores.registros).then((actuales) => {
              const lista = Array.isArray(actuales) ? actuales : [];
              const filtrados = lista.filter(item => item && item.idUsuario !== data.idUsuario && item.id !== data.id);
              filtrados.push(data);
              localforage.setItem(colecciones.localforage.empresa.trabajadores.registros, filtrados);
            }).catch(e => console.warn("Error auto-guardando trabajador en local:", e));
          }
          return data;
        } else {
          return null;
        }
      } catch (e) {
        console.warn("Falla de red en obtenerTrabajador, usando local:", e);
        const registros = await localforage.getItem(
          colecciones.localforage.empresa.trabajadores.registros,
        );
        return Array.isArray(registros)
          ? registros.find((item) => item && (item.idUsuario === idUsuario || item.id === idUsuario)) || null
          : null;
      }
    }
  },
  async crearVinculacion(
    idEmpresa,
    idUsuario,
    privilegios,
    cargo = "Trabajador",
    esPropietario = false,
    estado = "Pendiente",
    esInvitado = false,
    esContratista = false,
  ) {
    const refVinculacion = doc(collection(db, "riskmann_trabajadores"));
    await setDoc(doc(db, "riskmann_trabajadores", refVinculacion.id), {
      id: refVinculacion.id,
      idUsuario: idUsuario,
      idEmpresa: idEmpresa,
      idHorario: "",
      idCargo: "",
      cargo: cargo,
      modalidad: "",
      estado: estado,
      privilegios: privilegios,
      esInvitado: esInvitado,
      esPropietario: esPropietario,
      esContratista: esContratista,
      registro: {
        idUsuario: idUsuario,
        estado: "Creado",
        creacion: serverTimestamp(),
        actualizacion: serverTimestamp(),
      },
    });
  },
};
